import { checkWorkRecommendationAccess } from './workRecommendationAccess';
import { getTranslation } from '@/hooks/useTranslation';
import { useCoworkerStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';
import { reportWorkLaunchError } from '@/lib/reportWorkLaunchError';
import { blockIfWorkModeDisabled } from '@/lib/workModeAvailability';

export function hrefForCreatedWork(created) {
  if (created?.execution?.type === 'mac-opencode') {
    const { projectId, sessionId } = created.execution;
    if (typeof projectId !== 'string' || projectId.length !== 36 || !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(projectId) ||
        typeof sessionId !== 'string' || /^ses_[A-Za-z0-9]{1,124}$/.exec(sessionId)?.[0] !== sessionId) return null;
    return { pathname: '/local-work', params: { folder: projectId, session: sessionId } };
  }
  if (created?.conversationId) return `/c/${created.conversationId}`;
  if (created?.id) return `/coworker/${created.id}`;
  return null;
}

/** Leave the composer. replace avoids losing the race when /app remounts after send. */
export function navigateToCreatedWork(router, created) {
  const href = hrefForCreatedWork(created);
  if (!href || !router) return;
  const go = () => {
    if (typeof router.replace === 'function') router.replace(href);
    else router.push?.(href);
  };
  if (typeof queueMicrotask === 'function') queueMicrotask(go);
  else go();
}

/**
 * Start a new Work thread from a chat prompt (and optional uploaded files),
 * then navigate to it. Used by the while-typing chip (conversation) and the
 * post-response "Send to Work mode" card.
 */
export async function startWorkFromPrompt({
  prompt,
  fileIds = [],
  collectionId,
  agentId,
  router,
  source = 'work_mode_suggestion',
} = {}) {
  const trimmed = String(prompt || '').trim();
  const ids = Array.isArray(fileIds) ? fileIds.filter(Boolean).map(String) : [];
  if (!trimmed && !ids.length) return null;
  if (blockIfWorkModeDisabled()) return null;

  const coworkerState = useCoworkerStore.getState();
  try {
    const access = await checkWorkRecommendationAccess(trimmed, collectionId);
    if (!access.canLaunch) throw new Error(getTranslation(`messages.workModeSuggestion.access.${access.notice}`));
    const created = await coworkerState.createTask({
      prompt: trimmed,
      fileIds: ids,
      ...coworkerState.getComposerLaunchOptions(),
      ...(collectionId ? { collectionId } : {}),
      ...(agentId ? { agentId } : {}),
    });
    trackEvent('work', 'started_from_chat_suggestion', source);
    navigateToCreatedWork(router, created);
    return created;
  } catch (err) {
    reportWorkLaunchError(err, { source });
    return null;
  }
}

export default startWorkFromPrompt;
